/**
 * CreateDropdownView — content of the "+ Create" Popover (SE-5022).
 *
 * Two pressable rows: "Report" (navigates to the player's matches
 * selection) and "Note" (switches the visible tab to Reports & Notes
 * and signals auto-create-note). The dropdown closes on selection —
 * the container resets the popover state in each handler.
 *
 * Manual-player management actions (Edit / Delete / Transfer) live in
 * the separate meatball (⋯) menu, not here — "+ Create" stays scoped to
 * the create actions per the V1.6 design (SE-5179).
 * @remarks View component — no business logic.
 */

import { FileTextIcon, PencilLineIcon } from "lucide-react-native";
import { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** View props for the create dropdown body */
export interface CreateDropdownViewProps {
  /** Fires when "Report" is selected — container navigates + closes the popover */
  readonly onSelectReport: () => void;
  /** Fires when "Note" is selected — container sets reactive vars + closes the popover */
  readonly onSelectNote: () => void;
}

/**
 * Render the create-popover body.
 * @param props - View props
 * @param props.onSelectReport - Handler for the Report row
 * @param props.onSelectNote - Handler for the Note row
 */
const CreateDropdownView = ({
  onSelectReport,
  onSelectNote,
}: CreateDropdownViewProps) => (
  <Stack className="web:max-w-56 min-w-48 rounded-md border border-outline-subtle bg-surface-base p-2 shadow-soft-2">
    <Pressable
      onPress={onSelectReport}
      accessibilityRole="button"
      accessibilityLabel="Create a report"
      className="web:cursor-pointer rounded-none hover:bg-surface-raised"
    >
      <Stack direction="horizontal" className="items-center gap-3 px-3 py-2">
        <Icon as={FileTextIcon} size="sm" className="text-content-primary" />
        <Text variant="unstyled" className="text-title-sm text-content-primary">
          Report
        </Text>
      </Stack>
    </Pressable>
    <Pressable
      onPress={onSelectNote}
      accessibilityRole="button"
      accessibilityLabel="Create a note"
      className="web:cursor-pointer rounded-none hover:bg-surface-raised"
    >
      <Stack direction="horizontal" className="items-center gap-3 px-3 py-2">
        <Icon as={PencilLineIcon} size="sm" className="text-content-primary" />
        <Text variant="unstyled" className="text-title-sm text-content-primary">
          Note
        </Text>
      </Stack>
    </Pressable>
  </Stack>
);

CreateDropdownView.displayName = "CreateDropdownView";

export default memo(CreateDropdownView);
